import { useEffect, useState } from 'react';
import {
  UserPlus,
  UserCheck,
  UserX,
  KeyRound,
  Trash2,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { adminApi } from '@/features/admin/services/adminApi';
import { getErrorMessage } from '@/shared/services/api';
import { useAuthStore } from '@/app/store/authStore';
import { AdminUser, USER_ROLES, UserRole } from '@/shared/types/api';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { cn } from '@/shared/utils/cn';

const inviteSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});
type InviteFormData = z.infer<typeof inviteSchema>;

function fullName(u: AdminUser): string {
  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  return name.length > 0 ? name : '—';
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export function AdminUsers() {
  const currentUserId = useAuthStore((s) => s.user?.userId);
  const provisionUser = useAuthStore((s) => s.provisionUser);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [pwUser, setPwUser] = useState<AdminUser | null>(null);
  const [pwValue, setPwValue] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormData>({ resolver: zodResolver(inviteSchema) });

  const loadUsers = async () => {
    try {
      setLoading(true);
      setUsers(await adminApi.getUsers());
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const runAction = async (userId: string, action: () => Promise<void>, successMessage: string) => {
    setBusyId(userId);
    try {
      await action();
      toast.success(successMessage);
      await loadUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Action failed'));
    } finally {
      setBusyId(null);
    }
  };

  // SC-238. Derived rather than read straight off `role` so a response from a server that
  // predates the tiers still renders correctly.
  const roleOf = (u: AdminUser): UserRole => u.role ?? (u.isAdmin ? 'Admin' : 'General');

  const onChangeRole = (u: AdminUser, role: UserRole) => {
    if (role === roleOf(u)) return;
    runAction(u.userId, () => adminApi.setRole(u.userId, role), `Permissions set to ${role}`);
  };

  const onToggleActive = (u: AdminUser) =>
    runAction(
      u.userId,
      () => (u.isActive ? adminApi.deactivateUser(u.userId) : adminApi.activateUser(u.userId)),
      u.isActive ? 'Account deactivated' : 'Account activated'
    );

  const openSetPassword = (u: AdminUser) => {
    setPwUser(u);
    setPwValue('');
    setPwConfirm('');
  };

  const passwordError = (pw: string): string | null => {
    if (pw.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pw)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(pw)) return 'Password must contain a lowercase letter';
    if (!/[0-9]/.test(pw)) return 'Password must contain a digit';
    return null;
  };

  const submitSetPassword = async () => {
    if (!pwUser) return;
    const err = passwordError(pwValue);
    if (err) {
      toast.error(err);
      return;
    }
    if (pwValue !== pwConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    setPwSaving(true);
    try {
      await adminApi.setPassword(pwUser.userId, pwValue);
      toast.success(`Password updated for ${pwUser.email}`);
      setPwUser(null);
      await loadUsers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to set password'));
    } finally {
      setPwSaving(false);
    }
  };

  const onDelete = (u: AdminUser) => {
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    return runAction(u.userId, () => adminApi.deleteUser(u.userId), 'User deleted');
  };

  const onInvite = async (data: InviteFormData) => {
    setInviting(true);
    try {
      const errorMessage = await provisionUser(data.email, data.firstName, data.lastName);
      if (errorMessage === null) {
        toast.success(`Invitation sent to ${data.email}`);
        reset();
        setShowInvite(false);
        await loadUsers();
      } else {
        toast.error(errorMessage || 'Failed to invite user');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{users.length} account{users.length === 1 ? '' : 's'}</p>
        <button onClick={() => setShowInvite(true)} className="btn-primary gap-2">
          <UserPlus className="h-4 w-4" />
          Invite user
        </button>
      </div>

      {users.length === 0 ? (
        <div className="card">
          <EmptyState icon={Users} title="No users" description="Invite the first team member to get started." />
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-left">
              <thead>
                <tr className="bg-muted/50">
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">User</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Created</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Last login</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((u) => {
                  const isSelf = u.userId === currentUserId;
                  const busy = busyId === u.userId;
                  return (
                    <tr key={u.userId} className="hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{fullName(u)}</div>
                        <div className="text-sm text-muted-foreground">{u.email}{isSelf && ' (you)'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {roleOf(u) === 'Admin' && <span className="badge-brand">Admin</span>}
                          {roleOf(u) === 'ReadOnly' && <span className="badge-neutral">Read Only</span>}
                          <span className={cn(u.isActive ? 'badge-success' : 'badge-neutral')}>
                            {u.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {!u.emailConfirmed && <span className="badge-warning">Pending</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(u.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(u.lastLoginAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1" aria-busy={busy}>
                          {busy && <Spinner className="mr-1 h-4 w-4 text-muted-foreground" />}
                          {/* SC-238: three tiers, so a two-state toggle no longer expresses the
                              options. Disabled for the acting admin — the server refuses a
                              self-demotion anyway. */}
                          <select
                            value={roleOf(u)}
                            onChange={(e) => onChangeRole(u, e.target.value as UserRole)}
                            disabled={busy || isSelf}
                            className="input h-8 py-0 text-xs"
                            title={isSelf ? 'You cannot change your own permissions' : 'Permissions'}
                            aria-label={`Permissions for ${u.email}`}
                          >
                            {USER_ROLES.map((r) => (
                              <option key={r.value} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => onToggleActive(u)}
                            disabled={busy || isSelf}
                            className="btn-icon btn-ghost h-8 w-8"
                            title={u.isActive ? 'Deactivate' : 'Activate'}
                            aria-label={u.isActive ? `Deactivate ${u.email}` : `Activate ${u.email}`}
                          >
                            {u.isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => openSetPassword(u)}
                            disabled={busy}
                            className="btn-icon btn-ghost h-8 w-8"
                            title="Set password"
                            aria-label={`Set a new password for ${u.email}`}
                          >
                            <KeyRound className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => onDelete(u)}
                            disabled={busy || isSelf}
                            className="btn-icon btn-ghost h-8 w-8 text-danger hover:text-danger"
                            title="Delete user"
                            aria-label={`Delete ${u.email}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs animate-fade-in">
          <div className="surface-overlay w-full max-w-md animate-slide-up p-6 shadow-modal">
            <h2 className="mb-1 text-xl font-bold text-foreground">Invite user</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              They receive an activation email to confirm their address and set a password. Accounts start with standard access.
            </p>
            <form onSubmit={handleSubmit(onInvite)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="invite-first" className="label">First name</label>
                  <input id="invite-first" className="input mt-1.5" {...register('firstName')} placeholder="First name" />
                  {errors.firstName && <p className="mt-1 text-sm text-danger">{errors.firstName.message}</p>}
                </div>
                <div>
                  <label htmlFor="invite-last" className="label">Last name</label>
                  <input id="invite-last" className="input mt-1.5" {...register('lastName')} placeholder="Last name" />
                  {errors.lastName && <p className="mt-1 text-sm text-danger">{errors.lastName.message}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="invite-email" className="label">Email address</label>
                <input id="invite-email" type="email" className="input mt-1.5" {...register('email')} placeholder="you@company.com" />
                {errors.email && <p className="mt-1 text-sm text-danger">{errors.email.message}</p>}
              </div>
              <div className="mt-6 flex gap-2">
                <button type="button" onClick={() => { setShowInvite(false); reset(); }} className="btn-outline flex-1">
                  Cancel
                </button>
                <button type="submit" disabled={inviting} className="btn-primary flex-1">
                  {inviting && <Spinner className="text-primary-foreground" />}
                  {inviting ? 'Sending…' : 'Send invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pwUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs animate-fade-in">
          <div className="surface-overlay w-full max-w-md animate-slide-up p-6 shadow-modal">
            <h2 className="mb-1 text-xl font-bold text-foreground">Set password</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Set a new password for <span className="font-medium text-foreground">{pwUser.email}</span> directly. They can sign in with it immediately — share it securely; it is not emailed.
            </p>
            <form
              onSubmit={(e) => { e.preventDefault(); void submitSetPassword(); }}
              className="space-y-4"
            >
              <div>
                <label htmlFor="pw-new" className="label">New password</label>
                <input
                  id="pw-new"
                  type="password"
                  autoComplete="new-password"
                  className="input mt-1.5"
                  value={pwValue}
                  onChange={(e) => setPwValue(e.target.value)}
                  placeholder="At least 8 chars, with upper, lower & digit"
                />
              </div>
              <div>
                <label htmlFor="pw-confirm" className="label">Confirm password</label>
                <input
                  id="pw-confirm"
                  type="password"
                  autoComplete="new-password"
                  className="input mt-1.5"
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  placeholder="Re-enter the password"
                />
              </div>
              <div className="mt-6 flex gap-2">
                <button type="button" onClick={() => setPwUser(null)} className="btn-outline flex-1">
                  Cancel
                </button>
                <button type="submit" disabled={pwSaving} className="btn-primary flex-1">
                  {pwSaving && <Spinner className="text-primary-foreground" />}
                  {pwSaving ? 'Saving…' : 'Set password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
